// src/components/RateCard.jsx
//
// "RATE CARD" page (SideMenu's "Rate Card" nav item already links to
// /rate-card). Restyled to match the theme's rate-card.html: a searchable,
// sortable, filterable table (Class/Asset Group/Asset Element/Main
// Element/Sub Element + rows-per-page, all derived from the loaded rows)
// with an "+ Add Item" button that opens the theme's Add modal (the same
// Class -> Asset Group -> Asset Element -> Main Element -> Sub Element
// cascade, same fields/endpoints as AddRateCardItemModal.jsx) and an
// "Import Rate Data" button that opens a modal to bulk-load rows from an
// uploaded Excel file via /import_rate_card.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api as axios } from "../config/apiConfig";
import { API_ORIGIN } from "../config/apiConfig";
import { canAdd, canEdit, canDelete } from "../utils/permissions";

const API_BASE = API_ORIGIN;
const NEW_UNIT_VALUE = "__new_unit__";
const NEW_SUB_ELEMENT_VALUE = "__new_se__";

const SORT_FIELD_KEYS = {
  class: "Class",
  assetGroup: "Asset_Group",
  assetElement: "Asset_Element",
  mainElement: "Main_Element",
  subElement: "Sub_Element",
  shortDescription: "Short_Description",
  rate: "Rate"
};

export default function RateCard() {
  // Quick Search (Header.jsx) deep-links here as /rate-card?q=<term>.
  const [searchParams] = useSearchParams();

  // ---- existing Rate_Card rows (the table) --------------------------
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // ---- table search / filters / sort / pagination ---------------------
  const [search, setSearch] = useState(() => searchParams.get("q") || "");
  const [filterClass, setFilterClass] = useState("");
  const [filterAssetGroup, setFilterAssetGroup] = useState("");
  const [filterAssetElement, setFilterAssetElement] = useState("");
  const [filterMainElement, setFilterMainElement] = useState("");
  const [filterSubElement, setFilterSubElement] = useState("");
  // Default bumped from 10 to 25 -- 10 is no longer one of the page-size
  // options below (25/50/75/200/400/600).
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  // ---- Add Item modal ---------------------------------------------------
  const [addModalOpen, setAddModalOpen] = useState(false);

  const [classes, setClasses] = useState([]);
  const [assetGroups, setAssetGroups] = useState([]);
  const [assetElements, setAssetElements] = useState([]);
  const [mainElements, setMainElements] = useState([]);
  const [subElementOptions, setSubElementOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);

  const [selectedYear, setSelectedYear] = useState("");
  const [selectedOpCo, setSelectedOpCo] = useState("");
  const [selectedProjectName, setSelectedProjectName] = useState("");

  const [cId, setCId] = useState("");
  const [className, setClassName] = useState("");
  const [agId, setAgId] = useState("");
  const [agName, setAgName] = useState("");
  const [aeId, setAeId] = useState("");
  const [aeName, setAeName] = useState("");
  const [meId, setMeId] = useState("");
  const [meName, setMeName] = useState("");
  const [seId, setSeId] = useState("");
  const [seName, setSeName] = useState("");
  const [isNewSubElement, setIsNewSubElement] = useState(false);
  const [boqDivision, setBoqDivision] = useState("");

  const [shortDescription, setShortDescription] = useState("");
  const [unit, setUnit] = useState("");
  const [isNewUnit, setIsNewUnit] = useState(false);
  const [rate, setRate] = useState("");

  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState(null);

  // Success confirmation modal shown after Add/Edit completes -- the
  // form used to just silently reset/close/refresh with zero
  // user-facing confirmation. savedWasEdit picks the right message.
  const [showSavedModal, setShowSavedModal] = useState(false);
  const [savedWasEdit, setSavedWasEdit] = useState(false);

  // Which row the Add/Edit modal is editing (null while adding a new
  // one) -- the modal itself is shared between Add and Edit since the
  // fields, cascade, and validation are identical; only the submit
  // target (POST vs PUT) and a couple of labels differ.
  const [editingRow, setEditingRow] = useState(null);

  // View modal (read-only) -- every column is already in `rows` from
  // the one GET /rate-card call, so this needs no extra fetch.
  const [viewingRow, setViewingRow] = useState(null);

  // Delete-in-progress row id, so only that row's button shows "..."
  // instead of the whole table locking up.
  const [deletingId, setDeletingId] = useState(null);

  // Delete confirmation / error modals -- replaces the old
  // window.confirm()/alert() popups with the app's standard styled
  // teal modal (same layout as showSavedModal above). confirmDeleteRow
  // holds the row pending confirmation (null = modal hidden);
  // deleteErrorMsg holds the message for the error modal (null = hidden).
  const [confirmDeleteRow, setConfirmDeleteRow] = useState(null);
  const [deleteErrorMsg, setDeleteErrorMsg] = useState(null);

  // ---- Import Rate Data modal ------------------------------------------
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [pendingImportFile, setPendingImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState(null);

  // ---- Download Template (download a blank fill-in Excel from GET
  // /rate_card_template, matching /import_rate_card's own column
  // headers so download -> edit -> re-upload round-trips cleanly) ------
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  // ------------------------------------------------------------------
  // Load the Rate Card table
  // ------------------------------------------------------------------
  const loadRows = useCallback(() => {
    setLoadingRows(true);
    setLoadError(null);
    axios
      .get(`${API_BASE}/rate-card`)
      .then((res) => setRows(res.data?.data || []))
      .catch((err) => {
        console.error("Failed to load rate card:", err);
        setLoadError("Failed to load Rate Card data.");
      })
      .finally(() => setLoadingRows(false));
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    axios.get(`${API_BASE}/class`).then((res) => setClasses(res.data?.data || [])).catch(() => setClasses([]));
    axios.get(`${API_BASE}/get_units_rate`).then((res) => setUnitOptions(res.data?.data || [])).catch(() => setUnitOptions([]));
  }, []);

  // ------------------------------------------------------------------
  // Cascading dropdowns for the Add Item modal -- same endpoints as
  // AddRateCardItemModal.jsx.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!cId) return;
    const found = classes.find((c) => String(c.C_ID) === String(cId));
    if (found) setClassName(found.Class || "");
  }, [cId, classes]);

  useEffect(() => {
    if (!cId) { setAssetGroups([]); return; }
    axios.get(`${API_BASE}/get_asset_groups_rate?c_id=${encodeURIComponent(cId)}`)
      .then((res) => setAssetGroups(res.data?.data || []))
      .catch(() => setAssetGroups([]));
  }, [cId]);

  useEffect(() => {
    if (!cId || !agId) { setAssetElements([]); return; }
    axios.get(`${API_BASE}/get_asset_elements_rate?c_id=${encodeURIComponent(cId)}&ag_id=${encodeURIComponent(agId)}`)
      .then((res) => setAssetElements(res.data?.data || []))
      .catch(() => setAssetElements([]));
  }, [cId, agId]);

  useEffect(() => {
    if (!cId || !agId || !aeId) { setMainElements([]); return; }
    axios.get(`${API_BASE}/get_main_elements_rate?c_id=${encodeURIComponent(cId)}&ag_id=${encodeURIComponent(agId)}&ae_id=${encodeURIComponent(aeId)}`)
      .then((res) => setMainElements(res.data?.data || []))
      .catch(() => setMainElements([]));
  }, [cId, agId, aeId]);

  useEffect(() => {
    if (!cId || !agId || !aeId || !meId) { setSubElementOptions([]); return; }
    axios.get(`${API_BASE}/get_sub_elements_list_rate?c_id=${encodeURIComponent(cId)}&ag_id=${encodeURIComponent(agId)}&ae_id=${encodeURIComponent(aeId)}&me_id=${encodeURIComponent(meId)}`)
      .then((res) => setSubElementOptions(res.data?.data || []))
      .catch(() => setSubElementOptions([]));
  }, [cId, agId, aeId, meId]);

  // ------------------------------------------------------------------
  // Derived option lists (from the loaded rows) for the table's own
  // filter dropdowns -- separate from the Add modal's cascade above,
  // which comes from live API endpoints instead.
  // ------------------------------------------------------------------
  const uniqueValues = (key, list = rows) => {
    const set = new Set(list.map((r) => r[key]).filter(Boolean));
    return Array.from(set).sort();
  };

  // Per request, the "Filter By" dropdowns now CASCADE the same way the
  // Add Item modal's Class -> Asset Group -> Asset Element -> Main
  // Element -> Sub Element selects already do (see the cId/agId/aeId/meId
  // cascade above) -- each level's option list only offers values that
  // actually co-occur with whatever is picked in the level(s) ABOVE it,
  // instead of always listing every distinct value across the whole
  // (often several-thousand-row) rate card regardless of other filters.
  // Built from the already-loaded `rows` (no extra API calls needed,
  // unlike the Add modal's live cascade) by narrowing the row set one
  // filter at a time; a level is never narrowed by a filter BELOW it, so
  // e.g. picking a Sub Element doesn't feed back and hide Main Elements.
  const rowsForClass = useMemo(
    () => (filterClass ? rows.filter((r) => r.Class === filterClass) : rows),
    [rows, filterClass]
  );
  const rowsForAssetGroup = useMemo(
    () => (filterAssetGroup ? rowsForClass.filter((r) => r.Asset_Group === filterAssetGroup) : rowsForClass),
    [rowsForClass, filterAssetGroup]
  );
  const rowsForAssetElement = useMemo(
    () => (filterAssetElement ? rowsForAssetGroup.filter((r) => r.Asset_Element === filterAssetElement) : rowsForAssetGroup),
    [rowsForAssetGroup, filterAssetElement]
  );
  const rowsForMainElement = useMemo(
    () => (filterMainElement ? rowsForAssetElement.filter((r) => r.Main_Element === filterMainElement) : rowsForAssetElement),
    [rowsForAssetElement, filterMainElement]
  );

  const availableClasses = useMemo(() => uniqueValues("Class"), [rows]);
  const availableAssetGroups = useMemo(() => uniqueValues("Asset_Group", rowsForClass), [rowsForClass]);
  const availableAssetElements = useMemo(() => uniqueValues("Asset_Element", rowsForAssetGroup), [rowsForAssetGroup]);
  const availableMainElements = useMemo(() => uniqueValues("Main_Element", rowsForAssetElement), [rowsForAssetElement]);
  const availableSubElements = useMemo(() => uniqueValues("Sub_Element", rowsForMainElement), [rowsForMainElement]);

  // Distinct RateYear / OpCo / ProjectName already sitting in the loaded
  // rows -- used to default the Add modal's read-only Data Date/OpCo
  // Title/Project Name fields (see openAddModal below) to a REAL value
  // from the database instead of a hardcoded blank/current-year guess.
  // Per request these three stay non-editable/no dropdown, just showing
  // one actual distinct value rather than "—".
  const availableRateYears = useMemo(() => uniqueValues("RateYear"), [rows]);
  const availableOpCos = useMemo(() => uniqueValues("OpCo"), [rows]);
  const availableProjectNames = useMemo(() => uniqueValues("ProjectName"), [rows]);

  // ------------------------------------------------------------------
  // Search + filter + sort + paginate
  // ------------------------------------------------------------------
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filterClass && r.Class !== filterClass) return false;
      if (filterAssetGroup && r.Asset_Group !== filterAssetGroup) return false;
      if (filterAssetElement && r.Asset_Element !== filterAssetElement) return false;
      if (filterMainElement && r.Main_Element !== filterMainElement) return false;
      if (filterSubElement && r.Sub_Element !== filterSubElement) return false;
      if (q) {
        // RC_ID/Rate/RateYear added so a Quick Search hit on one of those
        // numeric columns (which the header search matches server-side)
        // still finds the row once it lands here with the same query
        // prefilled into this search box.
        const haystack = [
          r.Class, r.Asset_Group, r.Asset_Element, r.Main_Element,
          r.Sub_Element, r.Short_Description, r.Unit, r.OpCo, r.ProjectName,
          r.RC_ID, r.Rate, r.RateYear
        ].filter((v) => v !== null && v !== undefined && v !== "").join(" ").toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, filterClass, filterAssetGroup, filterAssetElement, filterMainElement, filterSubElement]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sortedRows = useMemo(() => {
    if (!sortField) return filteredRows;
    const key = SORT_FIELD_KEYS[sortField];
    const copy = [...filteredRows];
    copy.sort((a, b) => {
      if (key === "Rate") {
        return (Number(a.Rate || 0) - Number(b.Rate || 0)) * (sortDir === "asc" ? 1 : -1);
      }
      return String(a[key] || "").localeCompare(String(b[key] || "")) * (sortDir === "asc" ? 1 : -1);
    });
    return copy;
  }, [filteredRows, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / rowsPerPage));
  const pagedRows = sortedRows.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  useEffect(() => {
    setPage(1);
  }, [search, filterClass, filterAssetGroup, filterAssetElement, filterMainElement, filterSubElement, rowsPerPage]);

  const sortIndicator = (field) => (sortField !== field ? "⇅" : sortDir === "asc" ? "↑" : "↓");

  // ------------------------------------------------------------------
  // Add Item modal handlers
  // ------------------------------------------------------------------
  const resetAddForm = () => {
    setSelectedYear(""); setSelectedOpCo(""); setSelectedProjectName("");
    setCId(""); setClassName("");
    setAgId(""); setAgName("");
    setAeId(""); setAeName("");
    setMeId(""); setMeName("");
    setSeId(""); setSeName(""); setIsNewSubElement(false); setBoqDivision("");
    setShortDescription("");
    setUnit(""); setIsNewUnit(false);
    setRate("");
  };

  // Data Date / OpCo Title / Project Name are non-editable in this modal
  // (both Add and Edit) -- per request, these are fixed context values
  // rather than something picked here. Edit shows the row's own existing
  // values (set in openEditModal below); Add now defaults to the most
  // recent distinct RateYear already sitting in Rate_Card
  // (availableRateYears above), and to the first distinct OpCo/ProjectName --
  // previously these just started blank ("—") / at the current year
  // regardless of what the table's actual data looked like. Year uses the
  // MOST RECENT value (not availableRateYears[0], which is the oldest --
  // uniqueValues() sorts ascending) since Rate_Card can hold older/legacy
  // years alongside the current one (e.g. a leftover 2005 row next to
  // 2025), and the default should reflect the current data year.
  const openAddModal = () => {
    setAddError(null);
    setEditingRow(null);
    resetAddForm();
    setSelectedYear(
      availableRateYears.length
        ? String(availableRateYears[availableRateYears.length - 1])
        : String(new Date().getFullYear())
    );
    setSelectedOpCo(availableOpCos[0] || "");
    setSelectedProjectName(availableProjectNames[0] || "");
    setAddModalOpen(true);
  };

  const handleClassChange = (e) => {
    const val = e.target.value;
    const found = classes.find((c) => String(c.C_ID) === val);
    setCId(val);
    setClassName(found?.Class || "");
    setAgId(""); setAgName("");
    setAeId(""); setAeName("");
    setMeId(""); setMeName("");
    setSeId(""); setSeName(""); setIsNewSubElement(false); setBoqDivision("");
  };

  const handleAgChange = (e) => {
    const val = e.target.value;
    const found = assetGroups.find((a) => String(a.AG_ID) === val);
    setAgId(val);
    setAgName(found?.Asset_Group || "");
    setAeId(""); setAeName("");
    setMeId(""); setMeName("");
    setSeId(""); setSeName(""); setIsNewSubElement(false); setBoqDivision("");
  };

  const handleAeChange = (e) => {
    const val = e.target.value;
    const found = assetElements.find((a) => String(a.AE_ID) === val);
    setAeId(val);
    setAeName(found?.Asset_Element || "");
    setMeId(""); setMeName("");
    setSeId(""); setSeName(""); setIsNewSubElement(false); setBoqDivision("");
  };

  const handleMeChange = (e) => {
    const val = e.target.value;
    const found = mainElements.find((m) => String(m.ME_ID) === val);
    setMeId(val);
    setMeName(found?.Main_Element || "");
    setSeId(""); setSeName(""); setIsNewSubElement(false); setBoqDivision("");
  };

  const handleSeChange = (e) => {
    const val = e.target.value;
    if (val === NEW_SUB_ELEMENT_VALUE) {
      setIsNewSubElement(true);
      setSeId(""); setSeName("");
      setBoqDivision(subElementOptions[0]?.BOQ_Division || "");
      return;
    }
    setIsNewSubElement(false);
    const found = subElementOptions.find((s) => String(s.SE_ID) === val);
    setSeId(val);
    setSeName(found?.Sub_Element || "");
    setBoqDivision(found?.BOQ_Division || "");
  };

  const handleUnitChange = (e) => {
    const val = e.target.value;
    if (val === NEW_UNIT_VALUE) {
      setIsNewUnit(true);
      setUnit("");
      return;
    }
    setIsNewUnit(false);
    setUnit(val);
  };

  const handleRateChange = (e) => {
    const v = e.target.value;
    if (v === "" || /^\d*\.?\d*$/.test(v)) {
      setRate(v);
    } else {
      alert("You can enter numbers only.");
    }
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
    setAddError(null);
    setEditingRow(null);
    resetAddForm();
  };

  // Opens the same Add modal pre-filled with an existing row's values.
  // The cascade selects (Asset Group/Element/Main Element/Sub Element)
  // are only fetched once their parent id is set (see the useEffects
  // above), so setting every id here up front is enough -- each select
  // list arrives already containing the id being edited, and shows it
  // selected as soon as it loads.
  const openEditModal = (row) => {
    setAddError(null);
    setEditingRow(row);
    setSelectedYear(row.RateYear || "");
    setSelectedOpCo(row.OpCo || "");
    setSelectedProjectName(row.ProjectName || "");
    setCId(row.C_ID ?? "");
    setClassName(row.Class || "");
    setAgId(row.AG_ID ?? "");
    setAgName(row.Asset_Group || "");
    setAeId(row.AE_ID ?? "");
    setAeName(row.Asset_Element || "");
    setMeId(row.ME_ID ?? "");
    setMeName(row.Main_Element || "");
    setSeId(row.SE_ID ?? "");
    setSeName(row.Sub_Element || "");
    setIsNewSubElement(false);
    setBoqDivision(row.BOQ_Division || "");
    setShortDescription(row.Short_Description || "");
    setUnit(row.Unit || "");
    setIsNewUnit(false);
    setRate(row.Rate != null ? String(row.Rate) : "");
    setAddModalOpen(true);
  };

  const handleAdd = async () => {
    setAddError(null);

    if (!cId) { setAddError("Select a Class."); return; }
    if (!agId) { setAddError("Select an Asset Group."); return; }
    if (!aeId) { setAddError("Select an Asset Element."); return; }
    if (!meId) { setAddError("Select a Main Element."); return; }
    if (!seName) { setAddError("Select an existing Sub Element or choose \"+ New Sub Element\" and type one."); return; }
    if (!unit) { setAddError("Select or enter a Unit."); return; }
    if (rate === "" || Number.isNaN(Number(rate))) { setAddError("Enter a valid Rate."); return; }

    setSaving(true);
    try {
      const payload = {
        C_ID: cId,
        Class: className,
        BOQ_Division: boqDivision,
        AG_ID: agId,
        Asset_Group: agName,
        AE_ID: aeId,
        Asset_Element: aeName,
        ME_ID: meId,
        Main_Element: meName,
        SE_ID: isNewSubElement ? null : seId,
        Sub_Element: seName,
        Short_Description: shortDescription || seName,
        Unit: unit,
        Rate: Number(rate),
        OpCo: selectedOpCo || "",
        ProjectName: selectedProjectName || "",
        RateYear: selectedYear || new Date().getFullYear()
      };

      const res = editingRow
        ? await axios.put(`${API_BASE}/rate-card/${editingRow.RC_ID}`, payload)
        : await axios.post(`${API_BASE}/insert_rate_card`, payload);
      const data = res?.data;

      if (!data || data.success !== true) {
        setAddError(data?.error || (editingRow ? "Failed to save changes." : "Failed to add item."));
        setSaving(false);
        return;
      }

      setSaving(false);
      setSavedWasEdit(!!editingRow);
      setAddModalOpen(false);
      setShowSavedModal(true);
    } catch (err) {
      console.error(editingRow ? "update_rate_card failed:" : "insert_rate_card failed:", err);
      setAddError(err?.response?.data?.error || (editingRow ? "Failed to save changes." : "Failed to add item."));
      setSaving(false);
    }
  };

  // Deferred reset/refresh -- runs only once the user dismisses the
  // success modal, so the Add modal's fields don't visibly clear out
  // from behind the confirmation before it appears.
  const handleSavedModalOk = () => {
    setShowSavedModal(false);
    resetAddForm();
    setEditingRow(null);
    loadRows();
  };

  // Opens the standard styled confirmation modal instead of
  // window.confirm() -- the actual delete only happens once the user
  // clicks "Delete" in that modal (see confirmDeleteAction below).
  const openDeleteConfirm = (row) => {
    setConfirmDeleteRow(row);
  };

  const confirmDeleteAction = async () => {
    const row = confirmDeleteRow;
    if (!row) return;
    setConfirmDeleteRow(null);
    setDeletingId(row.RC_ID);
    try {
      const res = await axios.delete(`${API_BASE}/rate-card/${row.RC_ID}`);
      const data = res?.data;
      if (!data || data.success !== true) {
        setDeleteErrorMsg(data?.error || "Failed to delete item.");
        return;
      }
      setRows((prev) => prev.filter((r) => r.RC_ID !== row.RC_ID));
    } catch (err) {
      console.error("delete_rate_card failed:", err);
      setDeleteErrorMsg(err?.response?.data?.error || "Failed to delete item.");
    } finally {
      setDeletingId(null);
    }
  };

  // ------------------------------------------------------------------
  // Import Rate Data (Excel upload -> bulk insert into Rate_Card)
  // ------------------------------------------------------------------
  const closeImportModal = () => {
    setImportModalOpen(false);
    setPendingImportFile(null);
    setImportError(null);
    setImportResult(null);
  };

  const handleImportFilePicked = (e) => {
    const file = e.target.files && e.target.files[0];
    setPendingImportFile(file || null);
  };

  // Downloads GET /rate_card_template as a real .xlsx file. The API
  // client (`axios`, aliased from apiConfig's `api`) already attaches
  // the JWT via its request interceptor, which a plain <a href> download
  // link couldn't do -- so this fetches the file as a blob and triggers
  // the save through a throwaway <a>/object URL instead.
  const downloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const res = await axios.get(`${API_BASE}/rate_card_template`, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "Rate_Card_Template.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("rate_card_template failed:", err);
      window.alert("Could not download the template. Make sure the backend was restarted after the /rate_card_template route was added.");
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const submitImport = async () => {
    if (!pendingImportFile) {
      setImportError("Choose a file first.");
      return;
    }

    setImporting(true);
    setImportError(null);
    setImportResult(null);

    const form = new FormData();
    form.append("file", pendingImportFile);

    try {
      const res = await axios.post(`${API_BASE}/import_rate_card`, form, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      const data = res?.data;
      if (!data || data.success !== true) {
        setImportError(data?.error || "Import failed.");
      } else {
        setImportResult({
          inserted: data.inserted ?? 0,
          skipped: data.skipped ?? 0,
          errors: data.errors || []
        });
        setPendingImportFile(null);
        loadRows();
      }
    } catch (err) {
      console.error("import_rate_card failed:", err);
      setImportError(err?.response?.data?.error || "Import failed.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between page-title-head my-3 my-sm-0">
        <div className="w-100 w-sm-auto mb-2 mb-sm-0">
          {/* Per request, this is now a breadcrumb trail ("Cost
              Estimation / {page}"), same pattern and color (#02bc9c,
              16px) as the Cost Estimation wizard's Step1-7 breadcrumbs
              -- replaces the earlier #788CA5 bordered/uppercase title
              treatment. */}
          <nav aria-label="breadcrumb" className="d-inline-block">
            <span style={{ fontSize: 16, color: "#02bc9c" }}>Cost Estimation</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
            <span className="fw-semibold" style={{ fontSize: 16, color: "#02bc9c" }}>
              {/* Per request, the breadcrumb now shows the total rate
                  card count in parens -- same sortedRows.length already
                  used by the "Showing X to Y of Z ratecards" footer
                  below, so this always matches that number (reflects the
                  current filters, not just the unfiltered full table). */}
              Rate Card ({sortedRows.length})
            </span>
          </nav>
        </div>
        <div className="w-100 w-sm-auto">
          <div className="d-flex gap-2 justify-content-start justify-content-sm-end">
            {/* Add Item -- Contributor's Rate Card cell is "View only + Add
                Rate and Main Element", so Contributor keeps this button;
                View never reaches this page at all (route-guarded). */}
            {canAdd("RateCard") && (
              <button
                type="button"
                className="btn bg-green text-white btn-sm flex-grow-1 flex-sm-grow-0 text-nowrap"
                onClick={openAddModal}
                title="Add New Item"
              >
                <i className="ti ti-plus fs-lg me-1" />Add Item
              </button>
            )}
            {/* Download Template -> edit in Excel -> Import Rate Data is one
                workflow, so this is gated the same as Import below
                (Admin/Super Admin) rather than Add's lower bar. */}
            {canEdit("RateCard") && (
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm flex-grow-1 flex-sm-grow-0 text-nowrap"
                onClick={downloadTemplate}
                disabled={downloadingTemplate}
              >
                <i className="ti ti-file-spreadsheet me-1" /> {downloadingTemplate ? "Preparing..." : "Download Template"}
              </button>
            )}
            {/* Bulk Import can silently overwrite/skip many existing rows
                at once -- treated as an edit-level action, so it's
                Admin/Super Admin only even though Contributor can Add a
                single new item above. */}
            {canEdit("RateCard") && (
              <button
                type="button"
                className="btn btn-outline-primary btn-sm flex-grow-1 flex-sm-grow-0 text-nowrap"
                onClick={() => setImportModalOpen(true)}
              >
                <i className="ti ti-file-import me-1" /> Import Rate Data
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-nowrap border-light overflow-x-auto py-2 text-nowrap">
              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                <div className="app-search">
                  <input
                    className="form-control form-control-sm"
                    placeholder="Search rate items..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <i className="ti ti-search app-search-icon text-muted" />
                </div>
              </div>

              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                <span className="fw-semibold me-1 flex-shrink-0">Filter By:</span>

                <select
                  className="form-select form-select-sm"
                  style={{ width: 150 }}
                  value={filterClass}
                  onChange={(e) => {
                    // Changing a higher-level filter clears everything
                    // below it -- otherwise a now-stale lower selection
                    // (e.g. an Asset Group that doesn't exist under the
                    // newly picked Class) would silently filter the table
                    // down to zero rows instead of visibly resetting.
                    setFilterClass(e.target.value);
                    setFilterAssetGroup(""); setFilterAssetElement(""); setFilterMainElement(""); setFilterSubElement("");
                  }}
                >
                  <option value="">Class</option>
                  {availableClasses.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>

                <select
                  className="form-select form-select-sm"
                  style={{ width: 150 }}
                  value={filterAssetGroup}
                  onChange={(e) => {
                    setFilterAssetGroup(e.target.value);
                    setFilterAssetElement(""); setFilterMainElement(""); setFilterSubElement("");
                  }}
                >
                  <option value="">Asset Group</option>
                  {availableAssetGroups.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>

                <select
                  className="form-select form-select-sm"
                  style={{ width: 150 }}
                  value={filterAssetElement}
                  onChange={(e) => {
                    setFilterAssetElement(e.target.value);
                    setFilterMainElement(""); setFilterSubElement("");
                  }}
                >
                  <option value="">Asset Element</option>
                  {availableAssetElements.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>

                <select
                  className="form-select form-select-sm"
                  style={{ width: 150 }}
                  value={filterMainElement}
                  onChange={(e) => {
                    setFilterMainElement(e.target.value);
                    setFilterSubElement("");
                  }}
                >
                  <option value="">Main Element</option>
                  {availableMainElements.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>

                <select className="form-select form-select-sm" style={{ width: 150 }} value={filterSubElement} onChange={(e) => setFilterSubElement(e.target.value)}>
                  <option value="">Sub Element</option>
                  {availableSubElements.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>

                {/* Per request, options changed from 5/10/15/20 to
                    25/50/75/100/200/400/600 -- this table can hold
                    thousands of rate items, so larger page sizes are more
                    useful here than on smaller lists elsewhere in the app. */}
                <select className="form-select form-select-sm" style={{ width: 80 }} value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))}>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={75}>75</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={400}>400</option>
                  <option value={600}>600</option>
                </select>
              </div>
            </div>

            {loadError && <div className="alert alert-danger m-3">{loadError}</div>}

            <div className="table-responsive">
              {/* FIX: the table used to be `text-nowrap` on every cell,
                  which forced the long Short Description text onto one
                  line and made the whole table wider than its card --
                  that's what was pushing a horizontal scrollbar in below
                  the rows. Dropped table-wide text-nowrap; the few columns
                  that should still stay on one line (badges, numbers) get
                  it individually below, while Short Description wraps. */}
              {/* rate-card-table (new, scoped) -- per request, this table's
                  row background is #000000 at 20% opacity, and its hover
                  background is #02bc9c at 20% opacity, both via
                  themeOverrides.css. Added as its own class rather than
                  changing .table-custom/.table-hover directly, since those
                  are shared by the tables on ManageRoles.jsx / RoleMatrix.jsx
                  / UsersList.jsx / AIAssistant.jsx / EmailConfiguration.jsx /
                  ProjectList.jsx and shouldn't change color there too. */}
              <table className="table mb-0 table-centered table-custom table-hover rate-card-table w-100">
                <thead className="bg-light bg-opacity-25 thead-sm">
                  <tr className="fs-xxs text-uppercase">
                    <th className="text-nowrap" onClick={() => toggleSort("class")} role="button">Class {sortIndicator("class")}</th>
                    <th className="text-nowrap" onClick={() => toggleSort("assetGroup")} role="button">Asset Group {sortIndicator("assetGroup")}</th>
                    <th onClick={() => toggleSort("assetElement")} role="button">Asset Element {sortIndicator("assetElement")}</th>
                    <th className="text-nowrap" onClick={() => toggleSort("mainElement")} role="button">Main Element {sortIndicator("mainElement")}</th>
                    <th className="text-nowrap" onClick={() => toggleSort("subElement")} role="button">Sub Element {sortIndicator("subElement")}</th>
                    <th onClick={() => toggleSort("shortDescription")} role="button">Short Description {sortIndicator("shortDescription")}</th>
                    <th className="text-end text-nowrap" onClick={() => toggleSort("rate")} role="button">Rate (SAR) {sortIndicator("rate")}</th>
                    <th className="text-center text-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingRows && (
                    <tr><td colSpan={8} className="text-center py-4">Loading...</td></tr>
                  )}
                  {!loadingRows && pagedRows.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-4">No rate card items found.</td></tr>
                  )}
                  {!loadingRows && pagedRows.map((r) => (
                    <tr key={r.RC_ID}>
                      <td className="text-nowrap"><span className="badge bg-secondary-subtle text-secondary fw-semibold">{r.Class || "—"}</span></td>
                      <td className="text-nowrap">{r.Asset_Group || "—"}</td>
                      {/* Per request, wraps onto two lines instead of
                          forcing the row (and the whole table) wider for
                          long Asset Element names -- same idea as Short
                          Description below, just a narrower cap since
                          this column's values are usually much shorter. */}
                      <td style={{ whiteSpace: "normal", maxWidth: 180 }}>{r.Asset_Element || "—"}</td>
                      <td className="text-nowrap">{r.Main_Element || "—"}</td>
                      <td className="text-nowrap">{r.Sub_Element || "—"}</td>
                      {/* Wraps instead of forcing the row wide -- this is the
                          longest, most variable column (full item
                          descriptions), so it's the one that should break
                          onto multiple lines rather than stretch the table. */}
                      <td className="fw-medium text-dark" style={{ whiteSpace: "normal", minWidth: 260, maxWidth: 420 }}>
                        {r.Short_Description || "—"}
                      </td>
                      <td className="text-end fw-bold text-success text-nowrap">
                        {Number(r.Rate ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="text-nowrap">
                        {/* View reads straight from this already-loaded row (GET
                            /rate-card returns every column up front, so no extra
                            fetch is needed). Edit reopens the same Add modal
                            pre-filled, submitting to the new PUT /rate-card/<id>.
                            Delete calls the new DELETE /rate-card/<id>. */}
                        <div className="d-flex align-items-center gap-1 justify-content-center">
                          <button type="button" className="btn btn-icon btn-default btn-sm" title="View" onClick={() => setViewingRow(r)}>
                            <i className="ti fs-lg ti-eye" />
                          </button>
                          {/* Editing/deleting an EXISTING row -- Admin/Super
                              Admin only. Contributor can only Add a new item
                              (the button above), not touch what's already here. */}
                          {canEdit("RateCard") && (
                            <button type="button" className="btn btn-icon btn-default btn-sm" title="Edit" onClick={() => openEditModal(r)}>
                              <i className="ti fs-lg ti-edit" />
                            </button>
                          )}
                          {canDelete("RateCard") && (
                            <button
                              type="button"
                              className="btn btn-icon btn-default btn-sm"
                              title="Delete"
                              onClick={() => openDeleteConfirm(r)}
                              disabled={deletingId === r.RC_ID}
                            >
                              {deletingId === r.RC_ID ? "…" : <i className="ti fs-lg ti-trash" />}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="border-0 card-footer">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  Showing {sortedRows.length === 0 ? 0 : (page - 1) * rowsPerPage + 1} to{" "}
                  {Math.min(page * rowsPerPage, sortedRows.length)} of {sortedRows.length} ratecards
                </div>
                <ul className="pagination pagination-sm pagination-boxed m-0">
                  <li className={`page-item${page === 1 ? " disabled" : ""}`}>
                    <button type="button" className="page-link" onClick={() => setPage((p) => Math.max(1, p - 1))}>
                      <i className="ti ti-chevron-left" />
                    </button>
                  </li>
                  <li className="page-item active">
                    <span className="page-link">{page}</span>
                  </li>
                  <li className={`page-item${page === totalPages ? " disabled" : ""}`}>
                    <button type="button" className="page-link" onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                      <i className="ti ti-chevron-right" />
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ---- Add Item modal (theme's #addRateCardModal) ---- */}
      {addModalOpen && (
        <div className="modal show d-block" tabIndex="-1" role="dialog" aria-modal="true" style={{ background: "rgba(0,0,0,0.75)" }}>
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingRow ? "Edit Rate Card Item" : "Add New Rate Card Item"}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={closeAddModal} />
              </div>
              <div className="card mb-0">
                <div className="card-body">
                  {addError && <div className="alert alert-danger py-2 px-3 mb-3">{addError}</div>}

                  <div className="row g-3 mb-3">
                    <div className="col-md-3">
                      <label className="form-label fs-xs fw-semibold text-muted">Data Date</label>
                      <input type="text" className="form-control form-control-sm bg-light" value={selectedYear || "—"} readOnly />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fs-xs fw-semibold text-muted">OpCo Title</label>
                      <input type="text" className="form-control form-control-sm bg-light" value={selectedOpCo || "—"} readOnly />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fs-xs fw-semibold text-muted">Project Name</label>
                      <input type="text" className="form-control form-control-sm bg-light" value={selectedProjectName || "—"} readOnly />
                    </div>
                  </div>

                  {editingRow && (
                    <div className="alert alert-secondary py-2 px-3 mb-3 fs-xs">
                      Class, Asset Group, Asset Element, Main Element and Sub Element can't be changed
                      once an item exists — only Short Description, Unit and Rate can be edited here.
                    </div>
                  )}

                  <div className="row bg-disabled border border-light g-2 p-2 rounded mb-3">
                    <div className="col-12 col-sm-6 col-md">
                      <label className="form-label fs-xs fw-medium mb-1">Class</label>
                      <select className="form-select form-select-sm" value={cId} onChange={handleClassChange} disabled={!!editingRow}>
                        <option value="">Select</option>
                        {classes.map((c) => <option key={c.C_ID} value={c.C_ID}>{c.Class}</option>)}
                      </select>
                    </div>

                    <div className="col-12 col-sm-6 col-md">
                      <label className="form-label fs-xs fw-medium mb-1">Asset Group</label>
                      <select className="form-select form-select-sm" value={agId} onChange={handleAgChange} disabled={!!editingRow || !cId}>
                        <option value="">Select</option>
                        {assetGroups.map((a) => <option key={a.AG_ID} value={a.AG_ID}>{a.Asset_Group}</option>)}
                      </select>
                    </div>

                    <div className="col-12 col-sm-6 col-md">
                      <label className="form-label fs-xs fw-medium mb-1">Asset Element</label>
                      <select className="form-select form-select-sm" value={aeId} onChange={handleAeChange} disabled={!!editingRow || !agId}>
                        <option value="">Select</option>
                        {assetElements.map((a) => <option key={a.AE_ID} value={a.AE_ID}>{a.Asset_Element}</option>)}
                      </select>
                    </div>

                    <div className="col-12 col-sm-6 col-md">
                      <label className="form-label fs-xs fw-medium mb-1">Main Element</label>
                      <select className="form-select form-select-sm" value={meId} onChange={handleMeChange} disabled={!!editingRow || !aeId}>
                        <option value="">Select</option>
                        {mainElements.map((m) => <option key={m.ME_ID} value={m.ME_ID}>{m.Main_Element}</option>)}
                      </select>
                    </div>

                    <div className="col-12 col-sm-6 col-md">
                      <label className="form-label fs-xs fw-medium mb-1">Sub Element</label>
                      <select
                        className="form-select form-select-sm"
                        value={isNewSubElement ? NEW_SUB_ELEMENT_VALUE : seId}
                        onChange={handleSeChange}
                        disabled={!!editingRow || !meId}
                      >
                        <option value="">Select</option>
                        {subElementOptions.map((s) => <option key={s.SE_ID} value={s.SE_ID}>{s.Sub_Element}</option>)}
                        <option value={NEW_SUB_ELEMENT_VALUE}>+ New Sub Element…</option>
                      </select>
                      {isNewSubElement && (
                        <input
                          type="text"
                          className="form-control form-control-sm mt-1"
                          placeholder="Enter new Sub Element name"
                          value={seName}
                          onChange={(e) => setSeName(e.target.value)}
                        />
                      )}
                    </div>

                    <div className="col-md-6 mt-2">
                      <label className="form-label fs-xs fw-medium mb-1">Short Description</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={shortDescription}
                        onChange={(e) => setShortDescription(e.target.value)}
                        placeholder={seName || "Short description"}
                      />
                    </div>
                  </div>

                  <div className="row g-2 align-items-end">
                    <div className="col-md-2">
                      <label className="form-label fs-xs fw-medium mb-1">Currency</label>
                      <input type="text" className="form-control form-control-sm bg-light" value="SAR" readOnly />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fs-xs fw-medium mb-1">Unit</label>
                      <select className="form-select form-select-sm" value={isNewUnit ? NEW_UNIT_VALUE : unit} onChange={handleUnitChange}>
                        <option value="">Select</option>
                        {unitOptions.map((u) => <option key={u} value={u}>{u}</option>)}
                        <option value={NEW_UNIT_VALUE}>+ New Unit…</option>
                      </select>
                      {isNewUnit && (
                        <input
                          type="text"
                          className="form-control form-control-sm mt-1"
                          placeholder="e.g. m2"
                          value={unit}
                          onChange={(e) => setUnit(e.target.value)}
                        />
                      )}
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fs-xs fw-medium mb-1">Rate (SAR)</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="form-control form-control-sm"
                        value={rate}
                        onChange={handleRateChange}
                      />
                    </div>
                    <div className="col-md-2">
                      <button type="button" className="btn btn-primary btn-sm w-100" onClick={handleAdd} disabled={saving}>
                        {saving ? (editingRow ? "Saving..." : "Adding...") : editingRow ? "Save Changes" : "Add"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---- View item modal -- mirrors the Add/Edit modal's exact grid
          layout (same rows, same field groupings, same modal-xl width) so
          View and Edit look like the same screen. Every field here is a
          disabled + readOnly input rather than the real cascading
          Class/Asset Group/... <select> elements the Edit form uses --
          since nothing is editable, there's no need to wire up those
          dependent dropdown lookups; the plain value from the already-
          loaded row (GET /rate-card) is shown as static, non-interactive
          text styled to match. ---- */}
      {viewingRow && (
        <div className="modal show d-block" tabIndex="-1" role="dialog" aria-modal="true" style={{ background: "rgba(0,0,0,0.75)" }}>
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">View Rate Card Item</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setViewingRow(null)} />
              </div>
              <div className="card mb-0">
                <div className="card-body">
                  <div className="row g-3 mb-3">
                    <div className="col-md-3">
                      <label className="form-label fs-xs fw-semibold text-muted">Data Date</label>
                      <input type="text" className="form-control form-control-sm bg-light" value={viewingRow.RateYear || "—"} disabled readOnly />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fs-xs fw-semibold text-muted">OpCo Title</label>
                      <input type="text" className="form-control form-control-sm bg-light" value={viewingRow.OpCo || "—"} disabled readOnly />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fs-xs fw-semibold text-muted">Project Name</label>
                      <input type="text" className="form-control form-control-sm bg-light" value={viewingRow.ProjectName || "—"} disabled readOnly />
                    </div>
                  </div>

                  <div className="row bg-disabled border border-light g-2 p-2 rounded mb-3">
                    <div className="col-12 col-sm-6 col-md">
                      <label className="form-label fs-xs fw-medium mb-1">Class</label>
                      <input type="text" className="form-control form-control-sm bg-light" value={viewingRow.Class || "—"} disabled readOnly />
                    </div>
                    <div className="col-12 col-sm-6 col-md">
                      <label className="form-label fs-xs fw-medium mb-1">Asset Group</label>
                      <input type="text" className="form-control form-control-sm bg-light" value={viewingRow.Asset_Group || "—"} disabled readOnly />
                    </div>
                    <div className="col-12 col-sm-6 col-md">
                      <label className="form-label fs-xs fw-medium mb-1">Asset Element</label>
                      <input type="text" className="form-control form-control-sm bg-light" value={viewingRow.Asset_Element || "—"} disabled readOnly />
                    </div>
                    <div className="col-12 col-sm-6 col-md">
                      <label className="form-label fs-xs fw-medium mb-1">Main Element</label>
                      <input type="text" className="form-control form-control-sm bg-light" value={viewingRow.Main_Element || "—"} disabled readOnly />
                    </div>
                    <div className="col-12 col-sm-6 col-md">
                      <label className="form-label fs-xs fw-medium mb-1">Sub Element</label>
                      <input type="text" className="form-control form-control-sm bg-light" value={viewingRow.Sub_Element || "—"} disabled readOnly />
                    </div>
                    <div className="col-md-6 mt-2">
                      <label className="form-label fs-xs fw-medium mb-1">Short Description</label>
                      <input type="text" className="form-control form-control-sm bg-light" value={viewingRow.Short_Description || "—"} disabled readOnly />
                    </div>
                  </div>

                  <div className="row g-2 align-items-end">
                    <div className="col-md-2">
                      <label className="form-label fs-xs fw-medium mb-1">Currency</label>
                      <input type="text" className="form-control form-control-sm bg-light" value="SAR" disabled readOnly />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fs-xs fw-medium mb-1">Unit</label>
                      <input type="text" className="form-control form-control-sm bg-light" value={viewingRow.Unit || "—"} disabled readOnly />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fs-xs fw-medium mb-1">Rate (SAR)</label>
                      <input
                        type="text"
                        className="form-control form-control-sm bg-light"
                        value={Number(viewingRow.Rate ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        disabled
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={() => setViewingRow(null)}>Close</button>
                {canEdit("RateCard") && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => { const row = viewingRow; setViewingRow(null); openEditModal(row); }}
                  >
                    Edit
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---- Import Rate Data modal (theme's #importRateModal) ---- */}
      {importModalOpen && (
        <div className="modal show d-block" tabIndex="-1" role="dialog" aria-modal="true" style={{ background: "rgba(0,0,0,0.75)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import Rate Data</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={closeImportModal} />
              </div>
              <div className="modal-body">
                {importError && <div className="alert alert-danger">{importError}</div>}
                {importResult && (
                  <div className="alert alert-success">
                    Imported {importResult.inserted} row{importResult.inserted === 1 ? "" : "s"}
                    {importResult.skipped > 0 ? `, skipped ${importResult.skipped}` : ""}.
                    {importResult.errors.length > 0 && (
                      <div className="small mt-1">
                        {importResult.errors.slice(0, 5).map((e, i) => <div key={i}>• {e}</div>)}
                        {importResult.errors.length > 5 && <div>…and {importResult.errors.length - 5} more.</div>}
                      </div>
                    )}
                  </div>
                )}
                <div className="mb-3">
                  <label className="form-label">Upload Rate Card File (.csv, .xlsx)</label>
                  <input type="file" className="form-control" accept=".csv,.xlsx,.xls" onChange={handleImportFilePicked} />
                </div>
                {pendingImportFile && <p className="text-muted fs-xs m-0">Selected: {pendingImportFile.name}</p>}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={closeImportModal} disabled={importing}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={submitImport} disabled={importing}>
                  {importing ? "Importing..." : "Upload & Import"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---- Add/Edit success confirmation modal ---- */}
      {showSavedModal && (
        <div className="modal show d-block" tabIndex="-1" role="dialog" aria-modal="true" style={{ background: "rgba(0,0,0,0.75)" }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="border-0 modal-content text-white" style={{ backgroundColor: "#124d52", borderRadius: 4 }}>
              <div className="text-center modal-body p-4">
                <div className="mb-3">
                  <div className="align-items-center justify-content-center bg-white d-inline-flex rounded-circle" style={{ width: 48, height: 48 }}>
                    <i className="fs-2 text-dark ti ti-circle-check" style={{ fontStyle: "normal", fontWeight: 700 }} />
                  </div>
                </div>
                <h5 className="fs-5 fw-normal mb-4 modal-title">
                  {savedWasEdit ? "Rate card item updated successfully" : "Rate card item added successfully"}
                </h5>
                <div className="d-flex justify-content-center gap-3">
                  <button type="button" className="btn btn-primary" onClick={handleSavedModalOk}>
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---- Delete confirmation modal (replaces window.confirm) ---- */}
      {confirmDeleteRow && (
        <div className="modal show d-block" tabIndex="-1" role="dialog" aria-modal="true" style={{ background: "rgba(0,0,0,0.75)" }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="border-0 modal-content text-white" style={{ backgroundColor: "#124d52", borderRadius: 4 }}>
              <div className="text-center modal-body p-4">
                <div className="mb-3">
                  <div className="align-items-center justify-content-center bg-white d-inline-flex rounded-circle" style={{ width: 48, height: 48 }}>
                    <i className="fs-2 text-dark ti ti-trash" style={{ fontStyle: "normal", fontWeight: 700 }} />
                  </div>
                </div>
                <h5 className="fs-5 fw-normal mb-2 modal-title">
                  Delete "{confirmDeleteRow.Short_Description || confirmDeleteRow.Sub_Element}"?
                </h5>
                <p className="mb-4" style={{ opacity: 0.85 }}>
                  Deleted items cannot be recovered.
                </p>
                <div className="d-flex justify-content-center gap-3">
                  <button type="button" className="btn btn-outline-light" style={{ color: "#fff" }} onClick={() => setConfirmDeleteRow(null)}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-danger" onClick={confirmDeleteAction}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---- Delete error modal (replaces alert()) ---- */}
      {deleteErrorMsg && (
        <div className="modal show d-block" tabIndex="-1" role="dialog" aria-modal="true" style={{ background: "rgba(0,0,0,0.75)" }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="border-0 modal-content text-white" style={{ backgroundColor: "#124d52", borderRadius: 4 }}>
              <div className="text-center modal-body p-4">
                <div className="mb-3">
                  <div className="align-items-center justify-content-center bg-white d-inline-flex rounded-circle" style={{ width: 48, height: 48 }}>
                    <i className="fs-2 text-dark ti ti-alert-circle" style={{ fontStyle: "normal", fontWeight: 700 }} />
                  </div>
                </div>
                <h5 className="fs-5 fw-normal mb-4 modal-title">{deleteErrorMsg}</h5>
                <div className="d-flex justify-content-center gap-3">
                  <button type="button" className="btn btn-primary" onClick={() => setDeleteErrorMsg(null)}>
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
